import React from 'react';
import { ArrowRight, Award } from 'lucide-react';
import { Button } from './ui/button';
import { EditableText } from './inline/EditableText';

const tierOrder = ['platinum', 'gold', 'silver', 'standard'];

const SponsorsSection = ({ content, sectionId }) => {
  const data = {
    title: 'Our Sponsors',
    subtitle: 'Thank you to our supporting organizations',
    sponsors: [],
    cta: { label: 'Become a Sponsor', url: '/sponsorship', style: 'primary' },
    ...content,
  };
  const scope = (path) => ({ type: 'homepage_section', sectionId, path });

  const groupedSponsors = tierOrder
    .map((tier) => ({
      tier,
      label: tier.charAt(0).toUpperCase() + tier.slice(1),
      items: (data.sponsors || []).filter((s) => (s.tier || 'standard') === tier),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <section className="py-20 bg-white" data-testid="section-sponsors">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          {data.title && (
            <h2 className="text-3xl md:text-5xl font-bold text-[#0a1a2f] tracking-tight">
              <EditableText as="span" scope={scope('title')} value={data.title} multiline={false}>{data.title}</EditableText>
            </h2>
          )}
          {data.subtitle && (
            <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              <EditableText as="span" scope={scope('subtitle')} value={data.subtitle}>{data.subtitle}</EditableText>
            </p>
          )}
        </div>

        {groupedSponsors.length > 0 ? (
          groupedSponsors.map((group) => (
            <div key={group.tier} className="mb-12 last:mb-0">
              <h3 className="text-sm font-bold uppercase tracking-widest text-slate-400 mb-6 text-center">
                {group.label} Sponsors
              </h3>
              <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
                {group.items.map((sponsor, idx) => (
                  <a
                    key={idx}
                    href={sponsor.website_url || '#'}
                    target={sponsor.website_url ? '_blank' : undefined}
                    rel="noopener noreferrer"
                    className="group flex items-center justify-center p-4 rounded-xl hover:bg-slate-50 transition-colors"
                    data-testid={`sponsor-logo-${idx}`}
                  >
                    {sponsor.logo_url ? (
                      <img
                        src={sponsor.logo_url}
                        alt={sponsor.name}
                        className="max-h-16 md:max-h-20 w-auto object-contain grayscale group-hover:grayscale-0 transition-all duration-300"
                      />
                    ) : (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Award className="w-6 h-6" />
                        <span className="font-medium">{sponsor.name}</span>
                      </div>
                    )}
                  </a>
                ))}
              </div>
            </div>
          ))
        ) : (
          <p className="text-center text-slate-400 italic">Sponsors coming soon</p>
        )}

        {data.cta && data.cta.label && (
          <div className="mt-12 text-center">
            <a href={data.cta.url || '/sponsorship'}>
              <Button className="bg-[#00457f] hover:bg-[#003461] text-white px-8 py-3 rounded-full text-base font-semibold gap-2">
                <EditableText as="span" scope={scope('cta.label')} value={data.cta.label} multiline={false}>{data.cta.label}</EditableText>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default SponsorsSection;
